import { useEffect, useRef, type Dispatch, type SetStateAction } from 'react';
import type {
  FindingStatement,
  FollowUp,
  Report as MReport,
} from '@/models/report';
import {
  registerWorksheetHistoryApplier,
  type ConclusionState,
  type WorksheetSnapshot,
} from './worksheetApplier';

/** Flag / follow-up / tag state lives in useReportFlags, beside the report. */
export interface WorksheetFlagsState {
  selectedFlags: Set<string>;
  setSelectedFlags: Dispatch<SetStateAction<Set<string>>>;
  followUp: FollowUp | null;
  setFollowUp: Dispatch<SetStateAction<FollowUp | null>>;
}

interface Args {
  currentReport: MReport | null;
  setCurrentReport: Dispatch<SetStateAction<MReport | null>>;
  findings: Record<string, FindingStatement[]>;
  setFindings: Dispatch<SetStateAction<Record<string, FindingStatement[]>>>;
  conclusion: ConclusionState | null;
  setConclusion: Dispatch<SetStateAction<ConclusionState | null>>;
  flags?: WorksheetFlagsState;
}

const NO_FLAGS = new Set<string>();

/**
 * Registers the mounted worksheet as the history's worksheet applier
 * (AP-773 §3.2). Reads go through refs so a step created mid-render sees the
 * latest committed state; writes go through the functional setters so undo /
 * redo folds into the same state the editor, derived recalc, and autosave
 * already watch (R7: reverted state is just another unsaved edit).
 */
export function useWorksheetHistoryApplier({
  currentReport,
  setCurrentReport,
  findings,
  setFindings,
  conclusion,
  setConclusion,
  flags,
}: Args): void {
  const latest = useRef({ currentReport, findings, conclusion, flags });
  useEffect(() => {
    latest.current = { currentReport, findings, conclusion, flags };
  }, [currentReport, findings, conclusion, flags]);

  const setSelectedFlags = flags?.setSelectedFlags;
  const setFollowUp = flags?.setFollowUp;

  useEffect(
    () =>
      registerWorksheetHistoryApplier({
        getSnapshot: () => {
          const { currentReport: report, ...rest } = latest.current;
          if (!report) return null;
          return {
            measurements: report.measurements,
            aiMeasurements: report.aiMeasurements,
            context: report.context,
            tags: report.tags,
            findings: rest.findings,
            conclusion: rest.conclusion,
            selectedFlags: rest.flags?.selectedFlags ?? NO_FLAGS,
            followUp: rest.flags?.followUp ?? null,
          };
        },
        applySnapshot: (snapshot: WorksheetSnapshot) => {
          setCurrentReport((prev) =>
            prev
              ? {
                  ...prev,
                  measurements: snapshot.measurements,
                  aiMeasurements: snapshot.aiMeasurements,
                  context: snapshot.context,
                  tags: snapshot.tags,
                }
              : prev
          );
          setFindings(snapshot.findings);
          setConclusion(snapshot.conclusion);
          setSelectedFlags?.(new Set(snapshot.selectedFlags));
          setFollowUp?.(snapshot.followUp);
        },
      }),
    [
      setCurrentReport,
      setFindings,
      setConclusion,
      setSelectedFlags,
      setFollowUp,
    ]
  );
}
